import { useMemo } from 'react'
import { z } from 'zod'
import { createProductRegistry } from './registry'
import type { ProductRegistry } from './registry'
import {
  isExcludedRoute,
  CORE_SETTINGS_GROUPS_LEADING,
  CORE_SETTINGS_GROUPS_TRAILING,
  HOME_GROUP,
} from './nav'
import type { ConsoleProductManifest, NavGroup, NavItem, ProductKey, SettingsGroup } from './nav'
import type { HomeWidget, MaturitySignal } from './manifest'
import type { Identity } from './permissions'
import { canSee } from './permissions'
import { identityFor, useSession } from './session'

/**
 * Registry consumption — how the shell reads the product surface
 * (docs/product-manifest.md). NavRail, CommandPalette, the settings nav, the
 * home grid and the route guards all compose from here; no shell code imports
 * a manifest directly.
 *
 * INVERSION (E4): this module lives in `@bloo-team/core-shell`, and core never
 * names a product module (boundary law, constitution rule 11). So the
 * registry is INSTALLED, not imported: the app's COMPOSER
 * (`apps/console/src/product-registry.ts` — imported for its side effect by
 * `main.tsx` before anything renders) builds the manifest list and hands it
 * to {@link installProducts}. "Registered" means "what the composer
 * installed". Build profiles (E8) narrow that at build time: a subset build
 * composes a generated registry twin (`BLOO_PROFILE`, see
 * `docs/delivery-runbook.md`); the consumers below only ever see the
 * composed result.
 *
 * Before installation the registry is EMPTY — a deliberate zero-product
 * state (the home grid's empty state renders, nothing throws). Unit tests
 * install fixture manifests the same way the composer installs real ones.
 */

let registry: ProductRegistry<ConsoleProductManifest> = createProductRegistry([])

/**
 * Install the composed product set. Called once by the app's composer at
 * startup (and by tests, per suite). Validation — duplicate ids, unmet
 * `requires` — happens in {@link createProductRegistry} and throws here,
 * loudly, at composition time.
 */
export function installProducts(manifests: readonly ConsoleProductManifest[]): void {
  registry = createProductRegistry(manifests)
}

/** The installed registry (empty until the composer runs). */
export function productRegistry(): ProductRegistry<ConsoleProductManifest> {
  return registry
}

/** Every registered manifest, in display order. */
export function registeredManifests(): readonly ConsoleProductManifest[] {
  return registry.manifests
}

/** Registered product ids, in display order. */
export function registeredProducts(): readonly ProductKey[] {
  return registry.ids as readonly ProductKey[]
}

/** Registered ids as a set — the "everything registered" entitlement. */
export function allProducts(): ReadonlySet<ProductKey> {
  return new Set(registeredProducts())
}

/** Look a manifest up by id; undefined for an unknown id. */
export function productById(id: string): ConsoleProductManifest | undefined {
  return registry.get(id)
}

/** Whether `id` names a registered product. */
export function isProductKey(id: string): id is ProductKey {
  return registry.has(id)
}

/* ------------------------------------------------------------- page gating */

/**
 * The nav keys of every page the installed registry actually carries
 * (docs/page-gating.md).
 *
 * On a full build this is every product's every nav entry. On a build whose
 * profile holds a page back, the composer emits manifests with that nav entry
 * already filtered out — so this set narrows for free, and core-shell never
 * learns that build profiles exist (constitution rule 11). "Enabled" here is a
 * BUILD fact, not an entitlement: an enabled page may still be hidden from a
 * given identity by `roles`/`tiers`/`gate`.
 */
export function enabledPageKeys(): ReadonlySet<string> {
  return new Set(registeredManifests().flatMap((m) => m.nav.map((entry) => entry.key)))
}

/**
 * Whether a page shipped in this build. The guard for a cross-page link or a
 * worklist row that points at a page another surface owns (P4, P5) — the
 * `integrationActive` precedent one tier down: coupling declared, never
 * imported. An unknown key is NOT enabled, so a typo hides the affordance
 * rather than rendering a dead link.
 */
export function isPageEnabled(navKey: string): boolean {
  return registeredManifests().some((m) => m.nav.some((entry) => entry.key === navKey))
}

/**
 * Whether the integration between `a` and `b` is active for a session: both
 * registered AND both entitled (D9 — cross-product UI renders only when both
 * sides are present and entitled).
 */
export function integrationActive(
  a: string,
  b: string,
  entitled: ReadonlySet<ProductKey>,
): boolean {
  return registry.integrationActive(a, b, entitled)
}

/* ------------------------------------------------------------ entitlements */

/**
 * The products this identity is entitled to, intersected with what is
 * registered (requirements §5 step 2: profile ∩ session). Pure — for guards
 * and non-React callers; components use {@link useEntitledProducts}.
 */
export function entitledProducts(identity: Identity): ReadonlySet<ProductKey> {
  return new Set(registeredProducts().filter((id) => identity.products[id] !== null))
}

/**
 * Hook: registered products ∩ the session's entitlements for this cluster/
 * scope. Empty set while signed out. This is the active product set that
 * drives the rail, palette, settings groups, home grid and route guards.
 */
export function useEntitledProducts(clusterId: string, scopeId?: string): ReadonlySet<ProductKey> {
  const session = useSession()
  return useMemo(
    () => (session ? entitledProducts(identityFor(session, clusterId, scopeId)) : new Set<ProductKey>()),
    [session, clusterId, scopeId],
  )
}

/* ------------------------------------------------------------- composition */

/**
 * The rail: Home (core-owned), then one group per entitled product, in
 * registry order. An unentitled product is simply absent (D4) — the locked
 * placeholder rail of IA14 is retired.
 */
export function navGroupsFor(entitled: ReadonlySet<ProductKey>): NavGroup[] {
  return [
    HOME_GROUP,
    ...registeredManifests()
      .filter((m) => entitled.has(m.id))
      .map((m) => ({
        key: m.id,
        title: m.name,
        product: m.id,
        items: m.nav,
      })),
  ]
}

/** An entry survives only if its declared integration is active. */
const integrationOk = (owner: ProductKey, item: NavItem, entitled: ReadonlySet<ProductKey>) =>
  !item.integration || integrationActive(owner, item.integration, entitled)

/**
 * The settings nav: core Access/Tenancy groups, then the entitled products'
 * contributed sections (sections sharing a `key` merge into one group —
 * that is how every product lands under the single "Products" label), then
 * core Platform/Billing. Entries whose `integration` partner is missing or
 * unentitled are pruned here, so no consumer needs to know the rule.
 */
export function settingsGroupsFor(entitled: ReadonlySet<ProductKey>): SettingsGroup[] {
  const merged = new Map<string, SettingsGroup>()
  // Core groups are cloned per call: a product may contribute INTO one (see
  // below), and the constants must stay pristine for the next composition.
  const core = new Map<string, SettingsGroup>(
    [...CORE_SETTINGS_GROUPS_LEADING, ...CORE_SETTINGS_GROUPS_TRAILING].map((g) => [g.key, { ...g, items: g.items.map((i) => ({ ...i })) }]),
  )
  for (const m of registeredManifests()) {
    if (!entitled.has(m.id)) continue
    for (const section of m.settings) {
      const items = section.items
        .filter((item) => integrationOk(m.id, item, entitled))
        .map((item) =>
          item.children?.length
            ? { ...item, children: item.children.filter((c) => integrationOk(m.id, c, entitled)) }
            : item,
        )
      if (items.length === 0) continue
      const coreGroup = core.get(section.key)
      if (coreGroup) {
        // A section keyed like a CORE group (`platform`) lands in that group
        // rather than beside it — that is how Integrations (D1,
        // docs/integrations-requirements.md) is a platform section that the
        // SynthAI manifest happens to contribute. An item whose key matches
        // an existing core item merges its CHILDREN into it: the core item
        // keeps its title, icon, gate and place; the product supplies the
        // sub-sections. Anything else appends to the group.
        mergeInto(coreGroup, items)
        continue
      }
      const existing = merged.get(section.key)
      if (existing) existing.items.push(...items)
      else merged.set(section.key, { key: section.key, title: section.title, items: [...items] })
    }
  }
  const leading = CORE_SETTINGS_GROUPS_LEADING.map((g) => core.get(g.key)!)
  const trailing = CORE_SETTINGS_GROUPS_TRAILING.map((g) => core.get(g.key)!)
  return [...leading, ...merged.values(), ...trailing]
}

function mergeInto(group: SettingsGroup, items: readonly NavItem[]): void {
  for (const item of items) {
    const at = group.items.findIndex((i) => i.key === item.key)
    if (at < 0) {
      group.items.push(item)
      continue
    }
    const target = group.items[at]!
    const children = [...(target.children ?? []), ...(item.children ?? [])]
    group.items[at] = { ...target, ...(children.length ? { children } : {}) }
  }
}

/**
 * The same sections, flat. This is what gates a section (one predicate per
 * section, wherever it is grouped) and what the command palette and the
 * settings catch-all list — composed per entitlement so the two can never
 * disagree with the grouped nav about who may see what.
 */
export function settingsNavFor(entitled: ReadonlySet<ProductKey>): NavItem[] {
  return settingsGroupsFor(entitled).flatMap((group) => group.items)
}

/**
 * The settings groups this identity actually SEES: entitlement composition
 * (settingsGroupsFor) narrowed by the per-identity role/tier/gate filter,
 * with emptied groups dropped. This is the settings frame's nav and the
 * /settings landing redirect's table of contents — one function, so the two
 * can never disagree. (Lived in the settings route until the E4 extraction.)
 */
export function visibleGroups(identity: Identity | null): SettingsGroup[] {
  if (!identity) return []
  return settingsGroupsFor(entitledProducts(identity))
    .map((group) => ({
      ...group,
      items: group.items.filter((i) =>
        canSee(identity, { roles: i.roles, tiers: i.tiers, gate: i.gate, admitsNoAccess: i.admitsNoAccess }),
      ),
    }))
    .filter((group) => group.items.length > 0)
}

/** Palette actions contributed by the entitled products, in registry order. */
export function paletteActionsFor(entitled: ReadonlySet<ProductKey>) {
  return registeredManifests()
    .filter((m) => entitled.has(m.id))
    .flatMap((m) => m.palette)
}

/**
 * Home-grid widgets contributed by the entitled products, ordered by each
 * widget's `order` rank (ascending, stable) so instrument rows interleave
 * across products exactly as the pre-split home laid them out (D7). An
 * unentitled product's widgets are simply absent and the grid reflows.
 */
export function homeWidgetsFor(entitled: ReadonlySet<ProductKey>): HomeWidget[] {
  return registeredManifests()
    .filter((m) => entitled.has(m.id))
    .flatMap((m) => m.home)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

/* ------------------------------------------------- the deployment journey */

/**
 * Signals the APP contributes, outside any product (J6).
 *
 * Users, tokens, integrations and directory sync live in `apps/console/src/lib`
 * — app-owned, and core may not import them (rule 2). The composer already
 * names both sides, so it installs this group the way it installs manifests.
 * Empty until it does, which is the same deliberate zero state the registry
 * itself starts in.
 */
let platformSignals: readonly MaturitySignal[] = []

export function installPlatformSignals(signals: readonly MaturitySignal[]): void {
  platformSignals = signals
}

/**
 * Every maturity signal this session can show: the entitled products' own,
 * plus the app's platform group.
 *
 * An unentitled product leaves no trace (J8) — no rows, and nothing in the
 * phase percents, so a fabric-only build can still reach Level 4 on the
 * phases it has. Page gating is the second subtraction and cannot happen
 * here: a signal's action is not known until it has been read, so
 * {@link onShippedPages} applies it to the scored readings.
 */
export function maturitySignalsFor(entitled: ReadonlySet<ProductKey>): MaturitySignal[] {
  const fromProducts = registeredManifests()
    .filter((m) => entitled.has(m.id))
    .flatMap((m) => m.maturity ?? [])
  return [...fromProducts, ...platformSignals]
}

/**
 * Drop signals whose action leads to a page this build excludes. Applied to
 * the READINGS rather than the signals themselves, because the action is not
 * known until the signal has been read.
 */
export function onShippedPages<T extends { reading: { action?: { to: string } } }>(scored: readonly T[]): T[] {
  return scored.filter((s) => !s.reading.action || !isExcludedRoute(s.reading.action.to))
}

/* ---------------------------------------------------------- ?products= fake */

export type ParsedProductsParam =
  | { ok: true; products: ProductKey[] | null }
  | { ok: false; error: string }

/**
 * Parse the demo-session `?products=fabric,vantage` narrowing (requirements
 * §5 step 6; docs/product-manifest.md "Faking entitlements"). Absent param →
 * `products: null` (grant everything registered). Unknown ids are REJECTED,
 * not ignored — a typo that silently granted all products would invalidate
 * the entitlement e2e suite. The schema is built per call because the
 * registry is installed at startup, after this module initializes.
 */
export function parseProductsParam(search: string): ParsedProductsParam {
  const raw = new URLSearchParams(search).get('products')
  if (raw === null) return { ok: true, products: null }
  const known = registeredProducts()
  const reject = (): ParsedProductsParam => ({
    ok: false,
    error: `Invalid ?products= value "${raw}" — known products: ${known.join(', ')}.`,
  })
  if (known.length === 0) return reject()
  const productIdSchema = z.enum(known as [ProductKey, ...ProductKey[]])
  const ids = raw.split(',').map((s) => s.trim()).filter(Boolean)
  const parsed = z.array(productIdSchema).nonempty().safeParse(ids)
  if (!parsed.success) return reject()
  return { ok: true, products: [...new Set(parsed.data)] }
}
